/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type SessionRequest, type SessionRequestStatus } from '../../lib/api/session-requests.js';
import {
  useDeclineSessionRequest,
  useSessionRequests,
} from '../../lib/queries/session-requests.js';
import { formatIst } from '../../lib/utils/datetime.js';

import { ApproveRequestModal } from './session-request-approve-modal.js';

const STATUS_TONE: Readonly<Record<SessionRequestStatus, BadgeTone>> = {
  pending: 'amber',
  approved: 'green',
  declined: 'rose',
};
const COLS = ['patient', 'requestedBy', 'preferred', 'type', 'status'] as const;

function Row({
  item,
  onApprove,
}: {
  readonly item: SessionRequest;
  readonly onApprove: (r: SessionRequest) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const decline = useDeclineSessionRequest();
  const approve = useCallback(() => {
    onApprove(item);
  }, [onApprove, item]);
  const onDecline = useCallback(() => {
    decline.mutate({ id: item.id });
  }, [decline, item.id]);
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">
        {item.clientName ?? '—'}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {item.requestedByName ?? '—'}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {item.preferredDate !== null
          ? formatIst(`${item.preferredDate}T00:00:00.000Z`, 'dd MMM yyyy')
          : '—'}
        {item.preferredTime !== null ? ` · ${item.preferredTime}` : ''}
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{item.therapyType ?? '—'}</td>
      <td className="px-5 py-3">
        <Badge label={t(`book.statuses.${item.status}`)} tone={STATUS_TONE[item.status]} />
      </td>
      <td className="px-5 py-3 text-right">
        {item.status === 'pending' && (
          <div className="flex justify-end gap-1">
            <Button variant="primary" size="sm" onClick={approve}>
              {t('sessionRequests.approve')}
            </Button>
            <Button variant="ghost" size="sm" onClick={onDecline} disabled={decline.isPending}>
              {t('sessionRequests.decline')}
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

/** Admin → Session Requests review queue (parent Book Appointment). */
export function SessionRequestsPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useSessionRequests();
  const [approving, setApproving] = useState<SessionRequest | null>(null);
  const rows = query.data ?? [];
  const onApprove = useCallback((r: SessionRequest) => {
    setApproving(r);
  }, []);
  const onClose = useCallback(() => {
    setApproving(null);
  }, []);

  return (
    <div className="p-8">
      <PageHeader title={t('sessionRequests.title')} subtitle={t('sessionRequests.subtitle')} />
      <div className="mt-5">
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={rows.length === 0}
          loadingLabel={t('common.loading')}
          errorLabel={t('common.loadError')}
          emptyLabel={t('sessionRequests.empty')}
        >
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                <tr>
                  {COLS.map((c) => (
                    <th key={c} className="px-5 py-3 font-semibold">
                      {t(`sessionRequests.col.${c}`)}
                    </th>
                  ))}
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((item) => (
                  <Row key={item.id} item={item} onApprove={onApprove} />
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </div>
      <ApproveRequestModal request={approving} onClose={onClose} />
    </div>
  );
}
